import { useEffect, useRef } from 'react';
import { headTags, type PageMeta } from '@yuan-math/seo';

/**
 * Writes a route's metadata into the live document.
 *
 * The rules are all in `@yuan-math/seo`; this is only the hand that puts them
 * in the head. That split is why the tags a crawler reads in the prerendered
 * HTML and the tags a student's browser ends up with after three clicks are the
 * same tags — `headTags` produces both.
 *
 * Every tag it writes is marked `data-seo`, and every apply clears the previous
 * set first. Without that, navigating from a lesson to a concept would leave
 * two canonical links in the head, and two canonicals mean no canonical.
 */
export function applyPageMeta(doc: Document, meta: PageMeta): void {
  for (const stale of Array.from(doc.head.querySelectorAll('[data-seo]'))) {
    // The title element is kept and rewritten rather than replaced: removing it
    // leaves the tab briefly untitled, and `document.title` handles it anyway.
    if (stale.tagName !== 'TITLE') stale.remove();
  }

  doc.title = meta.title;

  for (const tag of headTags(meta)) {
    if (tag.tag === 'title') continue;

    if (tag.tag === 'jsonld') {
      const script = doc.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', '');
      // `textContent`, never `innerHTML`: the browser does not parse markup out
      // of it, so content containing a closing tag cannot escape the element.
      script.textContent = tag.json;
      doc.head.append(script);
      continue;
    }

    const element = doc.createElement(tag.tag);
    element.setAttribute('data-seo', '');
    for (const [name, value] of Object.entries(tag.attrs)) element.setAttribute(name, value);
    doc.head.append(element);
  }
}

/**
 * Applies `meta` for as long as it is rendered.
 *
 * Mounted once, in `Layout`, rather than once per page. A page cannot then
 * forget to describe itself, and the description it gets is `metaForPath` —
 * the same function `scripts/prerender.ts` calls for the static copy of that
 * URL, so the two cannot disagree.
 */
export function Seo({ meta }: { meta: PageMeta }): null {
  const latest = useRef(meta);
  latest.current = meta;

  useEffect(() => {
    applyPageMeta(document, latest.current);
  }, [meta]);

  return null;
}
